import { useState } from 'react';
import { EnvelopeSimple, Phone, MapPin, Clock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1707968781591-59ff287a54c6?w=1600" alt="Contact us" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-nexa-charcoal/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-medium text-white mb-4">Contact Us</h1>
            <p className="text-white/80">We would love to hear from you</p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="font-serif text-3xl font-medium text-nexa-charcoal mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="name">Name *</Label><Input id="name" name="name" value={formData.name} onChange={handleInputChange} className="mt-1" required data-testid="contact-name" /></div>
                  <div><Label htmlFor="email">Email *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} className="mt-1" required data-testid="contact-email" /></div>
                </div>
                <div><Label htmlFor="subject">Subject *</Label><Input id="subject" name="subject" value={formData.subject} onChange={handleInputChange} className="mt-1" required data-testid="contact-subject" /></div>
                <div><Label htmlFor="message">Message *</Label><Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} className="mt-1 min-h-[150px]" required data-testid="contact-message" /></div>
                <Button type="submit" className="btn-gold" disabled={loading} data-testid="contact-submit">{loading ? 'Sending...' : 'Send Message'}</Button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h2 className="font-serif text-3xl font-medium text-nexa-charcoal mb-6">Get in Touch</h2>
              <p className="text-muted-foreground mb-8">Have questions about our products or need assistance with your order? Our friendly customer support team is here to help.</p>
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-nexa-stone flex items-center justify-center flex-shrink-0"><MapPin size={24} className="text-nexa-gold" /></div>
                  <div><h3 className="font-semibold text-nexa-charcoal mb-1">Address</h3><p className="text-muted-foreground">712 Greenfield Avenue<br />Detroit, MI 11201</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-nexa-stone flex items-center justify-center flex-shrink-0"><Phone size={24} className="text-nexa-gold" /></div>
                  <div><h3 className="font-semibold text-nexa-charcoal mb-1">Phone</h3><a href="tel:+18883511122" className="text-muted-foreground hover:text-nexa-gold transition-colors">+1-888-351-1122</a></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-nexa-stone flex items-center justify-center flex-shrink-0"><EnvelopeSimple size={24} className="text-nexa-gold" /></div>
                  <div><h3 className="font-semibold text-nexa-charcoal mb-1">Email</h3><a href="mailto:support@nexaorbits.store" className="text-muted-foreground hover:text-nexa-gold transition-colors">support@nexaorbits.store</a></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-nexa-stone flex items-center justify-center flex-shrink-0"><Clock size={24} className="text-nexa-gold" /></div>
                  <div><h3 className="font-semibold text-nexa-charcoal mb-1">Business Hours</h3><p className="text-muted-foreground">Monday - Friday: 9:00 AM - 6:00 PM EST<br />Saturday: 10:00 AM - 4:00 PM EST<br />Sunday: Closed</p></div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden h-64">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2948.8399488869453!2d-83.05!3d42.33!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDE5JzQ4LjAiTiA4M8KwMDMnMDAuMCJX!5e0!3m2!1sen!2sus!4v1234567890" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Nexaorbits Location"></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
